import type { LanguageModelSession } from "./global";
import type {
  AIAvailability,
  AIDownloadOptions,
  AIPromptOptions,
  AIProvider,
  AISession,
  AISessionOptions,
  SystemRequirementsReport,
} from "./provider";
import { AISessionError } from "./provider";
import { checkSystemRequirements } from "./requirements";

class ChromeBuiltinSession implements AISession {
  constructor(private readonly inner: LanguageModelSession) {}

  prompt(text: string, opts?: AIPromptOptions): Promise<string> {
    return this.inner.prompt(text, opts);
  }

  promptStreaming(text: string, opts?: { signal?: AbortSignal }): AsyncIterable<string> {
    const stream = this.inner.promptStreaming(text, opts);
    return readableStreamToAsyncIterable(stream);
  }

  destroy(): void {
    this.inner.destroy();
  }
}

async function* readableStreamToAsyncIterable<T>(stream: ReadableStream<T>): AsyncIterable<T> {
  const reader = stream.getReader();
  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) return;
      if (value !== undefined) yield value;
    }
  } finally {
    // On an early `for await…of` break the consumer stops pulling; cancel()
    // signals the underlying source to stop producing AND releases the lock,
    // whereas releaseLock() alone would leave Chrome's generation running.
    // cancel() on an errored stream rejects with the stored error, so attach
    // .catch() (don't await) to swallow it and avoid masking an exception here.
    reader.cancel().catch(() => {});
  }
}

export class ChromeBuiltinAIProvider implements AIProvider {
  readonly id = "chrome-builtin" as const;

  async checkAvailability(opts?: { languages?: string[] }): Promise<AIAvailability> {
    // Caller orchestrates checkRequirements separately so callers don't pay two
    // `navigator.*` probes per cycle. Injecting "requirements-unmet" into the
    // pipeline is the consumer's responsibility (gate on `(await checkRequirements()).ok`).
    if (typeof globalThis.LanguageModel === "undefined") return "unsupported";
    try {
      const raw = await globalThis.LanguageModel.availability(
        // `.length` guard so a caller-supplied `[]` doesn't pass through —
        // truthy-check on the array itself would forward `{ languages: [] }`.
        opts?.languages?.length ? { languages: opts.languages } : undefined
      );
      return raw;
    } catch {
      return "unsupported";
    }
  }

  async checkRequirements(): Promise<SystemRequirementsReport> {
    return checkSystemRequirements();
  }

  async download(opts: AIDownloadOptions): Promise<void> {
    if (typeof globalThis.LanguageModel === "undefined") {
      throw new AISessionError(
        "not-available",
        "LanguageModel is not available in this environment"
      );
    }
    let session: LanguageModelSession | undefined;
    try {
      session = await globalThis.LanguageModel.create({
        signal: opts.signal,
        monitor: (m) => {
          m.addEventListener("downloadprogress", (e) => {
            opts.onProgress(e.loaded, e.total);
          });
        },
      });
    } catch (err) {
      // Wrap Chrome's raw DOMException("AbortError") / other errors as
      // AISessionError so consumers depend on our contract, not Chrome's.
      if (err instanceof DOMException && err.name === "AbortError") {
        throw new AISessionError("aborted", "download was aborted");
      }
      const message = err instanceof Error ? err.message : String(err);
      throw new AISessionError("unknown", message);
    } finally {
      // Release the session we created solely to drive the download; in finally so
      // a future await before this point can't leak the heavyweight session.
      session?.destroy();
    }
  }

  async createSession(opts?: AISessionOptions): Promise<AISession> {
    if (typeof globalThis.LanguageModel === "undefined") {
      throw new AISessionError(
        "not-available",
        "LanguageModel is not available in this environment"
      );
    }
    let inner: LanguageModelSession;
    try {
      inner = await globalThis.LanguageModel.create(opts);
    } catch (err) {
      // Wrap Chrome's raw DOMException as AISessionError so consumers depend on
      // our contract, not Chrome's (mirrors download()).
      if (err instanceof DOMException && err.name === "AbortError") {
        throw new AISessionError("aborted", "session creation was aborted");
      }
      const message = err instanceof Error ? err.message : String(err);
      throw new AISessionError("unknown", message);
    }
    return new ChromeBuiltinSession(inner);
  }
}
